/**
 * SVG-safe helpers for shape renderers.
 *
 * Shape manifests build SVG strings via template literals. Any user-controlled
 * string interpolated into an SVG attribute or text node must pass through
 * `escapeXml` to avoid attribute-break / element-break injection — and any
 * value used as a color attribute must pass through `safeHex` so a malicious
 * `props.fillColor` like `" onload="alert(1)" x="` can't escape the quote.
 *
 * SVG renderers run in two contexts: (1) live React/Konva canvas, where the
 * SVG goes through Konva's parser (likely safe but defense-in-depth), and
 * (2) export pipelines (PNG/PDF/SVG-download) where the raw SVG is consumed
 * by image renderers that DO interpret embedded scripts in some configs.
 * Treat both contexts as untrusted output sinks.
 */

const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;

/**
 * Escape `& < > " '` for safe inclusion as either an attribute value or a
 * text-node body in an SVG/XML document.
 */
export function escapeXml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Validate a value as a CSS hex color (`#abc`, `#aabbcc`, `#aabbccdd`).
 * Returns the original value if valid, the fallback otherwise. Use for any
 * fill/stroke/accent prop that ends up as an SVG attribute.
 */
export function safeHex(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value) ? value : fallback;
}
